import { useState } from "react";
import { MapPin, Phone, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { full_name: "", email: "", phone: "", message: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CONTACT_FIELD_LABELS = { full_name: "Full Name", email: "Email", message: "Message" };

function validateContactForm(form) {
  for (const field of ["full_name", "email", "message"]) {
    if (!String(form[field] || "").trim()) {
      return `${CONTACT_FIELD_LABELS[field]} is required.`;
    }
  }
  if (!EMAIL_RE.test(form.email.trim())) {
    return "Email: please enter a valid email address.";
  }
  return null;
}

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState(null);
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateContactForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/contact", form);
      toast.success("Message received. We'll be in touch soon.");
      setReferenceNumber(data.reference_number);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-cream" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <Reveal>
          <p className="label-eyebrow text-gold-muted mb-3">Get In Touch</p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-ink">
            Talk to Kingston Headquarters
          </h2>

          <div className="mt-8 space-y-5">
            <div className="flex items-start gap-3" data-testid="contact-address">
              <MapPin size={19} className="text-gold-muted shrink-0 mt-0.5" />
              <span className="text-base text-ink">115B Red Hills Road, Kingston 19, Jamaica</span>
            </div>
            <div className="flex items-start gap-3" data-testid="contact-phones">
              <Phone size={19} className="text-gold-muted shrink-0 mt-0.5" />
              <div className="text-base text-ink">
                <a href="tel:+18763958332" className="hover:text-gold-muted transition-colors">876-395-8332</a>
                {" / "}
                <a href="tel:+18768163230" className="hover:text-gold-muted transition-colors">876-816-3230</a>
              </div>
            </div>
          </div>

          <div className="mt-8 rounded-xl border border-ink/10 bg-cream-alt p-5">
            <p className="text-sm text-ink-soft">
              Satellite offices in Canada, China, the Caribbean, and the United States opening soon.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            noValidate
            data-testid="contact-form"
            className="rounded-2xl border border-gold/20 bg-white shadow-sm p-6 sm:p-8 text-ink"
          >
            {referenceNumber && (
              <div
                data-testid="contact-reference-confirmation"
                className="mb-5 rounded-xl border border-gold/30 bg-gold/10 p-4 text-sm text-ink"
              >
                Message received! Your reference number:{" "}
                <span className="font-mono font-semibold" data-testid="contact-reference-number">
                  {referenceNumber}
                </span>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label htmlFor="contact_full_name">Full Name</Label>
                <Input
                  id="contact_full_name"
                  data-testid="contact-input-full-name"
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
              <div>
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Phone</Label>
                <Input
                  id="contact_phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
              <div>
                <Label htmlFor="contact_message">Message</Label>
                <Textarea
                  id="contact_message"
                  rows={4}
                  data-testid="contact-textarea-message"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="mt-1.5 bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="mt-6 w-full sm:w-auto bg-gold hover:bg-gold-hover disabled:opacity-60 text-[#0A140F] font-display font-semibold text-sm px-8 py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Send Message
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
